import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import './WareEffort.css';
import { useTranslation } from 'react-i18next';
import WarEffortSpoilsDialog from './WarEffortSpoilsDialog';
import WarEffortPurposeSelection from './WarEffortPurposeSelection';
import WarEffortFortification from './WarEffortFortification';
import WarEffortSkeletonCrewSelection, { getSkeletonCrewBgStyle } from './WarEffortSkeletonCrewSelection';
import { FriendsIcon } from './friends';
import { SettingsMenu } from './settings';
import { useMusic } from './hooks/useMusic';
import { useMusic as useMusicContext } from './music/MusicContext';
import { playSoundEffect } from './hooks/useSoundEffects';
import MenuBackButton from './components/MenuBackButton';
import { getEffectiveSoundOutputVolume } from './hooks/soundEffectsManager';
import { API_URL } from './config/apiConfig';
import { WorldMap, getAllMappedCellIds } from './worldMap';
import { TutorialMessageBox, TutorialReplayButton, useMenuTutorialReplay } from './tutorial/index';
import { useFriends } from './friends';
import ConfirmationDialog from './notifications/ConfirmationDialog';
import NotificationToast from './notifications/NotificationToast';
import { useTutorialStatus } from './hooks/useTutorialStatus';
import { BATTLE_OVERVIEW_MODE_STICKER_KEYS } from './battleOverview/modeStickerKeys';
import { useDismissBattleOverviewModeSticker } from './battleOverview/useDismissBattleOverviewModeSticker';

function WareEffort({ user, onLogout }) {
  const navigate = useNavigate();
  const { t, i18n: i18nInstance } = useTranslation();

  const [currentMission, setCurrentMission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leavingMission, setLeavingMission] = useState(false);
  const [missionJustLeft, setMissionJustLeft] = useState(false);
  const [showSpoilsDialog, setShowSpoilsDialog] = useState(false);
  const [hasUnclaimedSpoils, setHasUnclaimedSpoils] = useState(false);
  const [warEffortData, setWarEffortData] = useState(null);
  const [showHQBuildingPage, setShowHQBuildingPage] = useState(false);
  const missionJustLeftRef = useRef(false);
  const inPurposeSelectionRef = useRef(false);
  const [showTutorialWorldMap, setShowTutorialWorldMap] = useState(false);
  const { shouldShowTutorial, markSeenLocally } = useTutorialStatus(user);
  useDismissBattleOverviewModeSticker(user, BATTLE_OVERVIEW_MODE_STICKER_KEYS.warEffort);
  const { isReplayRequest: isReplayingWorldMapTutorial } = useMenuTutorialReplay('war_effort_world_map', {
    enabled: Boolean(user) && !loading,
    onReplay: () => setShowTutorialWorldMap(true),
  });
  const [showLeaveConfirmDialog, setShowLeaveConfirmDialog] = useState(false);
  const [notification, setNotification] = useState(null);
  const [skeletonCrewActive, setSkeletonCrewActive] = useState(false);
  const [submitCrewLoading, setSubmitCrewLoading] = useState(false);
  const [submitCrewError, setSubmitCrewError] = useState(null);
  const [inviteAllyLoading, setInviteAllyLoading] = useState(false);
  const [inviteAllyError, setInviteAllyError] = useState(null);
  const [devSimulateLoading, setDevSimulateLoading] = useState(false);
  const [devResetLoading, setDevResetLoading] = useState(false);
  const { friends } = useFriends();
  const currentUserId = useMemo(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const decoded = jwtDecode(token);
        if (decoded.userId != null) return Number(decoded.userId);
      }
    } catch {}
    const ls = localStorage.getItem('userId');
    return ls != null ? Number(ls) : null;
  }, []);
  const isPlayer1 = currentMission != null && currentUserId != null && Number(currentMission.player1_id) === currentUserId;
  const isDevUser = user === 'player1';

  // Derive viewer purpose/faction from mission slot when status is waiting_crew (broadcast often omits viewer_purpose/viewer_faction)
  const waitingCrewViewerPurpose = useMemo(() => {
    if (!currentMission || (currentMission.mission_mode !== '2v2' && currentMission.mission_mode !== 'ffa') || currentMission.status !== 'waiting_crew' || currentUserId == null) return null;
    if (Number(currentMission.player1_id) === currentUserId) return currentMission.player1_purpose || null;
    if (Number(currentMission.player2_id) === currentUserId) return currentMission.player2_purpose || null;
    if (Number(currentMission.player3_id) === currentUserId) return currentMission.player3_purpose || null;
    if (Number(currentMission.player4_id) === currentUserId) return currentMission.player4_purpose || null;
    return null;
  }, [currentMission, currentUserId]);
  const waitingCrewViewerFaction = useMemo(() => {
    if (!currentMission || (currentMission.mission_mode !== '2v2' && currentMission.mission_mode !== 'ffa') || currentMission.status !== 'waiting_crew' || currentUserId == null) return null;
    const name = (Number(currentMission.player1_id) === currentUserId && (currentMission.player1_faction_name || currentMission.player1_faction))
      || (Number(currentMission.player2_id) === currentUserId && (currentMission.player2_faction_name || currentMission.player2_faction))
      || (Number(currentMission.player3_id) === currentUserId && (currentMission.player3_faction_name || currentMission.player3_faction))
      || (Number(currentMission.player4_id) === currentUserId && (currentMission.player4_faction_name || currentMission.player4_faction));
    return name || null;
  }, [currentMission, currentUserId]);
  const skeletonCrewPurpose = currentMission?.viewer_purpose ?? waitingCrewViewerPurpose;
  const skeletonCrewFaction = currentMission?.viewer_faction ?? waitingCrewViewerFaction;

  // FFA: when mission is already active but I haven't submitted crew yet (late joiner), show skeleton crew so I can submit and join
  const ffaActiveINeedCrew = useMemo(() => {
    if (!currentMission || currentMission.mission_mode !== 'ffa' || currentMission.status !== 'active' || currentUserId == null) return false;
    if (Number(currentMission.player1_id) === currentUserId) return !currentMission.player1_strategy_id;
    if (Number(currentMission.player2_id) === currentUserId) return !currentMission.player2_strategy_id;
    if (Number(currentMission.player3_id) === currentUserId) return !currentMission.player3_strategy_id;
    if (Number(currentMission.player4_id) === currentUserId) return !currentMission.player4_strategy_id;
    return false;
  }, [currentMission, currentUserId]);

  // Purpose/faction for my slot (FFA) when in crew screen - works for both waiting_crew and active (late joiner)
  const ffaMySlotPurpose = useMemo(() => {
    if (!currentMission || currentMission.mission_mode !== 'ffa' || currentUserId == null) return null;
    if (Number(currentMission.player1_id) === currentUserId) return currentMission.player1_purpose || null;
    if (Number(currentMission.player2_id) === currentUserId) return currentMission.player2_purpose || null;
    if (Number(currentMission.player3_id) === currentUserId) return currentMission.player3_purpose || null;
    if (Number(currentMission.player4_id) === currentUserId) return currentMission.player4_purpose || null;
    return null;
  }, [currentMission, currentUserId]);
  const ffaMySlotFaction = useMemo(() => {
    if (!currentMission || currentMission.mission_mode !== 'ffa' || currentUserId == null) return null;
    const name = (Number(currentMission.player1_id) === currentUserId && (currentMission.player1_faction_name || currentMission.player1_faction))
      || (Number(currentMission.player2_id) === currentUserId && (currentMission.player2_faction_name || currentMission.player2_faction))
      || (Number(currentMission.player3_id) === currentUserId && (currentMission.player3_faction_name || currentMission.player3_faction))
      || (Number(currentMission.player4_id) === currentUserId && (currentMission.player4_faction_name || currentMission.player4_faction));
    return name || null;
  }, [currentMission, currentUserId]);

  // FFA: I clicked Start mission (my slot has started_at set)
  const ffaMyStartedAt = useMemo(() => {
    if (!currentMission || currentMission.mission_mode !== 'ffa' || currentUserId == null) return null;
    if (Number(currentMission.player1_id) === currentUserId) return currentMission.player1_started_at || null;
    if (Number(currentMission.player2_id) === currentUserId) return currentMission.player2_started_at || null;
    if (Number(currentMission.player3_id) === currentUserId) return currentMission.player3_started_at || null;
    if (Number(currentMission.player4_id) === currentUserId) return currentMission.player4_started_at || null;
    return null;
  }, [currentMission, currentUserId]);

  // FFA waiting_crew: I have submitted skeleton crew (my slot has strategy_id). Used to hide skeleton crew once done.
  const ffaHaveSubmittedCrew = useMemo(() => {
    if (!currentMission || currentMission.mission_mode !== 'ffa' || currentMission.status !== 'waiting_crew' || currentUserId == null) return false;
    return (Number(currentMission.player1_id) === currentUserId && !!currentMission.player1_strategy_id) ||
      (Number(currentMission.player2_id) === currentUserId && !!currentMission.player2_strategy_id) ||
      (Number(currentMission.player3_id) === currentUserId && !!currentMission.player3_strategy_id) ||
      (Number(currentMission.player4_id) === currentUserId && !!currentMission.player4_strategy_id);
  }, [currentMission, currentUserId]);

  // Block Escape key during skeleton crew selection so players cannot exit and retry
  useEffect(() => {
    if (!skeletonCrewActive) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [skeletonCrewActive]);

  // War effort music (unchanged)
  useMusic('/music/war_effort.mp3');

  // Ambient sound on HQ building screen only (sound effect so it plays when sounds on, even if music off)
  const { soundsEnabled, soundsVolume, masterVolume } = useMusicContext();
  const ambientRef = useRef(null);
  useEffect(() => {
    if (!showHQBuildingPage || !soundsEnabled) {
      if (ambientRef.current) {
        ambientRef.current.pause();
        ambientRef.current.currentTime = 0;
        ambientRef.current = null;
      }
      return;
    }
    const ambient = new Audio('/sounds/background.mp3');
    ambient.loop = true;
    const v = getEffectiveSoundOutputVolume(0.5);
    if (v === null) {
      return undefined;
    }
    ambient.volume = v;
    ambientRef.current = ambient;
    const p = ambient.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {});
    }
    return () => {
      ambient.pause();
      ambient.currentTime = 0;
      if (ambientRef.current === ambient) ambientRef.current = null;
    };
  }, [showHQBuildingPage, soundsEnabled, soundsVolume, masterVolume]);
  useEffect(() => {
    if (ambientRef.current && showHQBuildingPage) {
      const v = getEffectiveSoundOutputVolume(0.5);
      if (v !== null) {
        ambientRef.current.volume = v;
      }
    }
  }, [soundsVolume, masterVolume, showHQBuildingPage]);

  // Check for unclaimed spoils
  const checkUnclaimedSpoils = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/spoils`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const spoils = await response.json();
        setHasUnclaimedSpoils(spoils.length > 0);
      }
    } catch (error) {
      console.error('Error checking spoils:', error);
    }
  }, []);
  
  // Fetch active mission for user
  const fetchActiveMission = useCallback(async (force = false) => {
    // Don't fetch if we just left a mission (prevent race condition)
    // Use ref to avoid stale closure issues
    if (missionJustLeftRef.current || missionJustLeft) {
      console.log('Skipping fetchActiveMission - mission was just left');
      return;
    }
    
    // Don't fetch if we're in purpose selection mode (currentMission is null)
    // unless we're forcing a fetch (e.g., on initial mount)
    // This prevents unnecessary fetches when user is selecting a purpose
    // Use ref to check this to avoid stale closures
    if (!force && (currentMission === null || inPurposeSelectionRef.current)) {
      console.log('Skipping fetchActiveMission - in purpose selection mode', { 
        currentMission, 
        inPurposeSelection: inPurposeSelectionRef.current 
      });
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Try to fetch active mission
      const missionResponse = await fetch(`${API_URL}/war-effort/active-mission`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (missionResponse.ok) {
        const mission = await missionResponse.json();
        if (!mission) {
          setCurrentMission(null);
        } else if (mission.status === 'abandoned') {
          // Double-check that mission status is not 'abandoned' (backend should filter this, but verify)
          console.log('Mission is abandoned, not setting as current mission');
          setCurrentMission(null);
        } else {
          // Verify that the current user is actually part of this mission
          // Get userId from JWT token (more reliable than localStorage, especially for private windows)
          let userIdNum = null;
          try {
            const token = localStorage.getItem('token');
            if (token) {
              const decoded = jwtDecode(token);
              userIdNum = decoded.userId ? Number(decoded.userId) : null;
            }
          } catch (error) {
            console.error('Error decoding token:', error);
            // Fallback to localStorage
            const userId = localStorage.getItem('userId');
            userIdNum = userId ? Number(userId) : null;
          }
          
          const player1Id = mission.player1_id ? Number(mission.player1_id) : null;
          const player2Id = mission.player2_id ? Number(mission.player2_id) : null;
          const player3Id = mission.player3_id ? Number(mission.player3_id) : null;
          const player4Id = mission.player4_id ? Number(mission.player4_id) : null;
          
          if (userIdNum && (userIdNum === player1Id || userIdNum === player2Id || userIdNum === player3Id || userIdNum === player4Id)) {
            setCurrentMission(mission);
          } else {
            console.warn('Current user is not part of this mission, ignoring it', {
              userId: userIdNum,
              player1Id,
              player2Id,
              missionId: mission.id
            });
            setCurrentMission(null);
          }
        }
      } else if (missionResponse.status === 404) {
        // Legacy: older servers returned 404 for no mission
        setCurrentMission(null);
      } else {
        console.error('Error fetching active mission:', missionResponse.status);
        setCurrentMission(null);
      }
      
      // Also fetch war effort status for other data
      const statusResponse = await fetch(`${API_URL}/war-effort/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (statusResponse.ok) {
        const data = await statusResponse.json();
        setWarEffortData(data);
      }
    } catch (error) {
      console.error('Error fetching war effort data:', error);
      setCurrentMission(null);
    } finally {
      setLoading(false);
    }
  }, [missionJustLeft, currentMission]);

  const fetchActiveMissionRef = useRef(fetchActiveMission);
  const checkUnclaimedSpoilsRef = useRef(checkUnclaimedSpoils);
  const currentMissionRef = useRef(currentMission);
  fetchActiveMissionRef.current = fetchActiveMission;
  checkUnclaimedSpoilsRef.current = checkUnclaimedSpoils;
  currentMissionRef.current = currentMission;

  const POLL_INTERVAL_MS = 30 * 1000; // 30s (was 2 min) - fallback when WebSocket updates are missed
  const lastFetchRef = useRef(0);
  const MIN_FETCH_INTERVAL_MS = 25000; // 25s (was 45s)

  // This poll is what eventually makes neutral_fortifications visible: GET /war-effort/active-mission returns them, setCurrentMission(mission) updates the prop to WarEffortFortification. Without an immediate refetch when showing the map, neutrals only appear after ~30s.

  useEffect(() => {
    // Only fetch on mount if we haven't just left a mission and not in purpose selection
    // Force fetch on initial mount to check for existing mission
    if (!missionJustLeft && !missionJustLeftRef.current && !inPurposeSelectionRef.current) {
      fetchActiveMission(true);
      lastFetchRef.current = Date.now();
    }
    checkUnclaimedSpoils();

    const interval = setInterval(() => {
      if (missionJustLeftRef.current || missionJustLeft || inPurposeSelectionRef.current) return;
      const now = Date.now();
      if (now - lastFetchRef.current < MIN_FETCH_INTERVAL_MS) return;
      lastFetchRef.current = now;
      if (currentMissionRef.current !== null) {
        fetchActiveMissionRef.current(false);
      }
      checkUnclaimedSpoilsRef.current();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [missionJustLeft]); // Refs for callbacks - avoid re-run loop when mission updates change fetchActiveMission

  // When we show the world map, refetch active mission once so neutral_fortifications are visible immediately.
  // Key by (missionId, status) so we refetch when transitioning to 'active' (game just started) and get neutrals right away.
  const lastMapRefetchKeyRef = useRef(null);
  useEffect(() => {
    const m = currentMissionRef.current;
    if (!m?.id) return;
    if (m.mission_mode === 'ffa' && m.status === 'waiting_players') return;
    if (m.mission_mode === '2v2' && ['waiting_ally', 'waiting_opponents', 'waiting_opponent_ally'].includes(m.status)) return;
    if ((m.mission_mode === '2v2' || m.mission_mode === 'ffa') && m.status === 'waiting_crew') return;
    const key = `${m.id}-${m.status}`;
    if (lastMapRefetchKeyRef.current === key) return;
    lastMapRefetchKeyRef.current = key;
    fetchActiveMissionRef.current(true);
    lastFetchRef.current = Date.now();
  }, [currentMission?.id, currentMission?.status, currentMission?.mission_mode]);
  
  const handlePurposeSelected = (mission) => {
    // We're leaving purpose selection mode, clear the flag
    inPurposeSelectionRef.current = false;
    setCurrentMission(mission);
  };
  
  const handleMissionUpdate = (mission) => {
    // Mission update means we have an active mission, not in purpose selection
    inPurposeSelectionRef.current = false;
    setCurrentMission(mission);
  };

  // 2v2/FFA: submit skeleton crew then refresh mission. FFA allows submit when waiting_crew, active (late join), or waiting_players (I clicked Start mission).
  const handle2v2SubmitCrew = useCallback(async (skeletonCrewOrderIds) => {
    if (!currentMission?.id) return;
    const canSubmit = currentMission.status === 'waiting_crew'
      || (currentMission.mission_mode === 'ffa' && (currentMission.status === 'active' || currentMission.status === 'waiting_players'));
    if (!canSubmit) return;
    let purpose = currentMission.viewer_purpose;
    let selectedFaction = currentMission.viewer_faction || currentMission.viewer_faction_name;
    if (!purpose || !selectedFaction) {
      if (currentUserId != null) {
        if (Number(currentMission.player1_id) === currentUserId) {
          purpose = purpose || currentMission.player1_purpose;
          selectedFaction = selectedFaction || currentMission.player1_faction_name || currentMission.player1_faction;
        } else if (Number(currentMission.player2_id) === currentUserId) {
          purpose = purpose || currentMission.player2_purpose;
          selectedFaction = selectedFaction || currentMission.player2_faction_name || currentMission.player2_faction;
        } else if (Number(currentMission.player3_id) === currentUserId) {
          purpose = purpose || currentMission.player3_purpose;
          selectedFaction = selectedFaction || currentMission.player3_faction_name || currentMission.player3_faction;
        } else if (Number(currentMission.player4_id) === currentUserId) {
          purpose = purpose || currentMission.player4_purpose;
          selectedFaction = selectedFaction || currentMission.player4_faction_name || currentMission.player4_faction;
        }
      }
    }
    if (!purpose || !selectedFaction) {
      setSubmitCrewError(t('warEffort.purposeSelection.missingPurposeOrFaction'));
      return;
    }
    try {
      setSubmitCrewLoading(true);
      setSubmitCrewError(null);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/mission/${currentMission.id}/submit-crew`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ skeletonCrewOrderIds, selectedFaction })
      });
      if (response.ok) {
        const updated = await response.json();
        setCurrentMission(updated);
        // FFA: after green "Start mission" (submit crew), go straight to world map — no second blue button
        if (updated?.mission_mode === 'ffa' && updated?.status === 'waiting_crew') {
          try {
            const startRes = await fetch(`${API_URL}/war-effort/mission/${currentMission.id}/start-ffa-game`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
            });
            if (startRes.ok) {
              const started = await startRes.json();
              // Fetch active mission and use it so neutral_fortifications are in state before we show the map (avoids needing a refresh).
              try {
                const getRes = await fetch(`${API_URL}/war-effort/active-mission?t=${Date.now()}`, {
                  headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
                });
                if (getRes.ok) {
                  const mission = await getRes.json();
                  if (mission && mission.status !== 'abandoned') {
                    setCurrentMission(mission);
                    lastFetchRef.current = Date.now();
                    return;
                  }
                }
              } catch (_) { /* use started if fetch fails */ }
              setCurrentMission(started);
              fetchActiveMissionRef.current(true);
              lastFetchRef.current = Date.now();
            }
          } catch (e) {
            console.error('Error starting FFA mission after crew:', e);
          }
        }
      } else {
        const data = await response.json();
        setSubmitCrewError(data.error || t('warEffort.purposeSelection.failedToSubmitCrew'));
      }
    } catch (err) {
      console.error('Submit crew error:', err);
      setSubmitCrewError(err.message || t('warEffort.purposeSelection.failedToSubmitCrew'));
    } finally {
      setSubmitCrewLoading(false);
    }
  }, [currentMission, currentUserId, t]);


  const handleInviteAlly = useCallback(async (friendId) => {
    if (!currentMission?.id) return;
    try {
      setInviteAllyLoading(true);
      setInviteAllyError(null);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/mission/${currentMission.id}/invite-ally`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ friendId: Number(friendId) })
      });
      if (response.ok) {
        const updated = await response.json();
        setCurrentMission(updated);
      } else {
        const data = await response.json();
        setInviteAllyError(data.error || t('warEffort.purposeSelection.failedToInviteAlly'));
      }
    } catch (err) {
      console.error('Invite ally error:', err);
      setInviteAllyError(err.message || t('warEffort.purposeSelection.failedToInviteAlly'));
    } finally {
      setInviteAllyLoading(false);
    }
  }, [currentMission?.id, t]);

  const handleDevSimulate2v2Join = useCallback(async (missionIdParam) => {
    const missionId = missionIdParam ?? currentMission?.id;
    if (!missionId) return;
    try {
      setDevSimulateLoading(true);
      setInviteAllyError(null);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/mission/${missionId}/dev-simulate-2v2-join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const updated = await response.json();
        setCurrentMission(updated);
      } else {
        const data = await response.json().catch(() => ({}));
        const msg = data.error || response.statusText || t('warEffort.purposeSelection.failedToSimulateJoin');
        setInviteAllyError(data.code ? `${msg} [${data.code}]` : msg);
      }
    } catch (err) {
      console.error('Dev simulate 2v2 join:', err);
      setInviteAllyError(err.message || t('warEffort.purposeSelection.failedToSimulateJoin'));
    } finally {
      setDevSimulateLoading(false);
    }
  }, [currentMission?.id, t]);

  const handleDevResetToWaitingAlly = useCallback(async () => {
    if (!currentMission?.id) return;
    try {
      setDevResetLoading(true);
      setInviteAllyError(null);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/mission/${currentMission.id}/dev-reset-to-waiting-ally`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const updated = await response.json();
        setCurrentMission(updated);
      } else {
        const data = await response.json();
        setInviteAllyError(data.error || t('warEffort.purposeSelection.failedToReset'));
      }
    } catch (err) {
      console.error('Dev reset to waiting_ally:', err);
      setInviteAllyError(err.message || t('warEffort.purposeSelection.failedToReset'));
    } finally {
      setDevResetLoading(false);
    }
  }, [currentMission?.id, t]);

  const handleReady = useCallback(async () => {
    if (!currentMission?.id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/mission/${currentMission.id}/ready`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const updated = await response.json();
        setCurrentMission(updated);
      } else {
        const data = await response.json().catch(() => ({}));
        console.error('Failed to mark ready:', data.error || response.statusText);
      }
    } catch (err) {
      console.error('Error marking ready:', err);
    }
  }, [currentMission?.id, t]);

  const handleStartCrewSelection = useCallback(async () => {
    if (!currentMission?.id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/mission/${currentMission.id}/start-crew-selection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const updated = await response.json();
        setCurrentMission(updated);
      } else {
        const data = await response.json().catch(() => ({}));
        console.error('Failed to start crew selection:', data.error || response.statusText);
      }
    } catch (err) {
      console.error('Error starting crew selection:', err);
    }
  }, [currentMission?.id, t]);

  const handleStartFFAGame = useCallback(async () => {
    if (!currentMission?.id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/mission/${currentMission.id}/start-ffa-game`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const updated = await response.json();
        // Fetch full mission so neutral_fortifications are in state before map shows (avoids needing refresh)
        try {
          const getRes = await fetch(`${API_URL}/war-effort/active-mission?t=${Date.now()}`, {
            headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
          });
          if (getRes.ok) {
            const mission = await getRes.json();
            if (mission && mission.status !== 'abandoned') {
              setCurrentMission(mission);
              lastFetchRef.current = Date.now();
              return;
            }
          }
        } catch (_) { /* use updated if fetch fails */ }
        setCurrentMission(updated);
      } else {
        const data = await response.json().catch(() => ({}));
        console.error('Failed to start FFA game:', data.error || response.statusText);
      }
    } catch (err) {
      console.error('Error starting FFA game:', err);
    }
  }, [currentMission?.id, t]);

  // Listen for mission-matched and mission-update from WebSocket (via FriendsProvider)
  useEffect(() => {
    const handler = (e) => {
      const mission = e?.detail?.mission;
      if (mission && (mission.player1_id || mission.player2_id)) {
        inPurposeSelectionRef.current = false;
        setCurrentMission(mission);
      }
    };
    window.addEventListener('war-effort-mission-update', handler);
    return () => window.removeEventListener('war-effort-mission-update', handler);
  }, []);
  
  const handleLeaveMissionClick = () => {
    playSoundEffect('/sounds/click.mp3');
    setShowLeaveConfirmDialog(true);
  };

  const handleLeaveMissionConfirm = async () => {
    setShowLeaveConfirmDialog(false);
    if (leavingMission) return;
    
    try {
      setLeavingMission(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        setNotification({ message: t('warEffort.leaveMissionNotLoggedIn'), type: 'error' });
        setLeavingMission(false);
        return;
      }
      
      console.log('Leaving mission...');
      const response = await fetch(`${API_URL}/war-effort/leave-mission`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const responseData = await response.json();
      
      if (response.ok) {
        console.log('Mission left successfully:', responseData);
        // Set flag to prevent polling from re-fetching mission
        // Use both state and ref to ensure we catch all cases
        missionJustLeftRef.current = true;
        setMissionJustLeft(true);
        // Mark that we're now in purpose selection mode
        inPurposeSelectionRef.current = true;
        // Clear current mission immediately - this will show purpose selection
        // Use a function form to ensure we're clearing the state
        setCurrentMission(prev => {
          console.log('Clearing current mission, previous value:', prev);
          return null;
        });
        // Refresh war effort status data (but not mission - we just left it)
        // Do this in the background without blocking
        const token = localStorage.getItem('token');
        fetch(`${API_URL}/war-effort/status`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
          .then(statusResponse => {
            if (statusResponse.ok) {
              return statusResponse.json();
            }
          })
          .then(data => {
            if (data) {
              setWarEffortData(data);
            }
          })
          .catch(error => {
            console.error('Error refreshing war effort status:', error);
          });
        // Clear the flag after a delay to allow backend update to propagate
        // Also verify the mission is actually gone before clearing the flag
        setTimeout(async () => {
          try {
            const token = localStorage.getItem('token');
            const verifyResponse = await fetch(`${API_URL}/war-effort/active-mission`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            let missionGone = verifyResponse.status === 404;
            if (!missionGone && verifyResponse.ok) {
              const verified = await verifyResponse.json();
              missionGone = !verified;
            }
            if (missionGone) {
              // Mission is confirmed gone, safe to resume polling
              console.log('Mission confirmed abandoned, resuming polling');
              missionJustLeftRef.current = false;
              setMissionJustLeft(false);
              // Keep inPurposeSelectionRef true - we're in purpose selection mode
              // This will be cleared when user selects a new purpose
            } else {
              // Mission still exists, keep flag set for longer
              console.log('Mission still exists, extending flag duration');
              setTimeout(() => {
                missionJustLeftRef.current = false;
                setMissionJustLeft(false);
                // Keep inPurposeSelectionRef true
              }, 5000); // Another 5 seconds
            }
          } catch (error) {
            console.error('Error verifying mission status:', error);
            // On error, clear flag anyway after delay
            setTimeout(() => {
              missionJustLeftRef.current = false;
              setMissionJustLeft(false);
              // Keep inPurposeSelectionRef true
            }, 5000);
          }
        }, 2000); // Check after 2 seconds
        setNotification({ message: t('warEffort.leaveMissionSuccess'), type: 'success' });
      } else {
        console.error('Failed to leave mission:', responseData);
        setNotification({ message: t('warEffort.leaveMissionFailed', { message: responseData.error || 'Unknown error' }), type: 'error' });
      }
    } catch (error) {
      console.error('Error leaving mission:', error);
      setNotification({ message: t('warEffort.leaveMissionFailed', { message: error.message || 'Please try again.' }), type: 'error' });
    } finally {
      setLeavingMission(false);
    }
  };
  
  // Get faction background for world map
  const factionColors = {
    'Remnants of Humanity': '#556B2F',
    'Exolot Dominion': '#4B0082',
    'League of Shadows': '#1A1A1A',
    'Ra\'s Children': '#F4A460',
    'Asrah': '#8B0000',
    'The Sentients': '#ADD8E6',
    'Combined': '#FFA500'
  };
  
  const getFactionBackground = (faction) => {
    if (!faction) return '#808080';
    const color = factionColors[faction];
    if (!color) return '#808080';
    
    // Convert hex to rgba
    const hexToRgba = (hex, alpha = 0.25) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };
    
    if (color.includes('gradient')) {
      return color.replace(/#([0-9A-Fa-f]{6})/g, (match, hex) => {
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, 1.0)`;
      });
    } else {
      return hexToRgba(color, 1.0);
    }
  };
  
  // Create cell owners map from war effort data
  const cellOwnersMap = useMemo(() => {
    if (!warEffortData?.cells) return {};
    const map = {};
    warEffortData.cells.forEach(cell => {
      if (cell.owner_name) {
        map[String(cell.cell_id)] = cell.owner_name;
      }
    });
    return map;
  }, [warEffortData]);
  
  const beaconCells = useMemo(() => {
    if (!warEffortData?.cellsWithWaitingPlayers) return [];
    return warEffortData.cellsWithWaitingPlayers.map(Number);
  }, [warEffortData]);

  // Show world map tutorial when world map first shows (have mission, not on HQ building page)
  useEffect(() => {
    if (currentMission && !showHQBuildingPage && shouldShowTutorial('war_effort_world_map')) {
      const timer = setTimeout(() => setShowTutorialWorldMap(true), 600);
      return () => clearTimeout(timer);
    }
  }, [currentMission, showHQBuildingPage, shouldShowTutorial]);
  
  const showPurposeSetupScreen =
    !currentMission ||
    (currentMission.mission_mode === '2v2' &&
      ['waiting_ally', 'waiting_opponents', 'waiting_opponent_ally'].includes(currentMission.status)) ||
    (currentMission.mission_mode === 'ffa' && currentMission.status === 'waiting_players');

  const showInMissionGame =
    !!currentMission && !showPurposeSetupScreen &&
    !((currentMission.mission_mode === '2v2' && currentMission.status === 'waiting_crew') ||
      (currentMission.mission_mode === 'ffa' && currentMission.status === 'waiting_crew' && !ffaHaveSubmittedCrew) ||
      (currentMission.mission_mode === 'ffa' && ffaActiveINeedCrew));

  return (
    <div className={`wareffort-container${showPurposeSetupScreen ? ' wareffort-container--purpose-setup' : ''}${showInMissionGame ? ' wareffort-container--in-mission' : ''}`}>
      {/* Standard header with Friends, Settings, User name */}
      <div className="wareffort-header-controls">
        <FriendsIcon />
        <SettingsMenu onLogout={() => {
          onLogout();
          navigate('/');
        }} />
        <span className="wareffort-header-username">{typeof user === 'string' ? user : (user?.username ?? '')}</span>
      </div>

      {/* Back button: from building page return to world map; otherwise to battleoverview; unavailable during skeleton crew selection */}
      <MenuBackButton
        onClick={() => {
          if (skeletonCrewActive) {
            return;
          }
          if (showHQBuildingPage) {
            setShowHQBuildingPage(false);
          } else {
            navigate('/battleoverview');
          }
        }}
        disabled={skeletonCrewActive || (currentMission?.mission_mode === '2v2' && currentMission?.status === 'waiting_crew')}
        aria-disabled={skeletonCrewActive || (currentMission?.mission_mode === '2v2' && currentMission?.status === 'waiting_crew')}
      />

      {/* Leave Mission button (if active mission exists; hidden on HQ building page) */}
      {currentMission && !showHQBuildingPage && (
        <button
          onClick={handleLeaveMissionClick}
          disabled={leavingMission}
          className={`wareffort-leave-mission-btn${leavingMission ? ' wareffort-leave-mission-btn--leaving' : ''}`}
        >
          {leavingMission ? t('warEffort.leaving') : t('warEffort.leaveMissionButton')}
        </button>
      )}
      
      {/* Claim Spoils button (if available) */}
      {hasUnclaimedSpoils && (
        <button
          className="spoils-button"
          onClick={() => setShowSpoilsDialog(true)}
        >
          Claim Spoils
        </button>
      )}
      
      {loading && !currentMission ? (
        <div className="wareffort-loading">
          Loading...
        </div>
      ) : showPurposeSetupScreen ? (
        <WarEffortPurposeSelection
          user={user}
          onPurposeSelected={handlePurposeSelected}
          onSkeletonCrewActiveChange={setSkeletonCrewActive}
          mission={currentMission && (currentMission.mission_mode === '2v2' || currentMission.mission_mode === 'ffa') ? currentMission : null}
          onMissionUpdate={handleMissionUpdate}
          onInviteAlly={handleInviteAlly}
          inviteAllyLoading={inviteAllyLoading}
          inviteAllyError={inviteAllyError}
          friends={friends}
          isPlayer1={isPlayer1}
          isDevUser={isDevUser}
          onDevSimulate2v2Join={handleDevSimulate2v2Join}
          devSimulateLoading={devSimulateLoading}
          onDevResetToWaitingAlly={handleDevResetToWaitingAlly}
          devResetLoading={devResetLoading}
          onReady={handleReady}
          onStartCrewSelection={handleStartCrewSelection}
          onStartFFAGame={handleStartFFAGame}
        />
      ) : ((currentMission.mission_mode === '2v2' && currentMission.status === 'waiting_crew') || (currentMission.mission_mode === 'ffa' && currentMission.status === 'waiting_crew' && !ffaHaveSubmittedCrew) || (currentMission.mission_mode === 'ffa' && ffaActiveINeedCrew)) ? (
        (() => {
          const purpose = skeletonCrewPurpose || ffaMySlotPurpose;
          const faction = skeletonCrewFaction || ffaMySlotFaction;
          return purpose && faction ? (
            <WarEffortSkeletonCrewSelection
              purpose={purpose}
              selectedFaction={faction}
              backgroundStyle={getSkeletonCrewBgStyle()}
              onComplete={handle2v2SubmitCrew}
              onBack={null}
              isSubmitting={submitCrewLoading}
              submitError={submitCrewError}
            />
          ) : (
            <div className="skeleton-crew-viewport-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', color: '#ccc', flexDirection: 'column', gap: '1rem' }}>
              <div className="skeleton-crew-fullscreen-background" style={getSkeletonCrewBgStyle()} />
              <p style={{ position: 'relative', zIndex: 1 }}>Loading your mission…</p>
              <p style={{ position: 'relative', zIndex: 1, fontSize: '0.9rem' }}>If you haven’t set your purpose and faction yet, do so in the lobby before the creator starts the game.</p>
            </div>
          );
        })()
      ) : (
        <WarEffortFortification
          mission={currentMission}
          user={user}
          onBack={() => setCurrentMission(null)}
          onFortificationComplete={handleMissionUpdate}
          onLogout={onLogout}
          showBuildingPage={showHQBuildingPage}
          onBuildingPageChange={setShowHQBuildingPage}
        />
      )}
      
      {/* Spoils Dialog */}
      <WarEffortSpoilsDialog
        isOpen={showSpoilsDialog}
        onClose={() => {
          setShowSpoilsDialog(false);
          checkUnclaimedSpoils();
        }}
        userId={typeof user === 'string' ? user : (user?.id || localStorage.getItem('userId'))}
        previewMode={false}
      />

      {showLeaveConfirmDialog && createPortal(
        <ConfirmationDialog
          message={t('warEffort.leaveMissionConfirm')}
          confirmText={t('warEffort.leave')}
          cancelText={t('warEffort.stay')}
          type="warning"
          onConfirm={handleLeaveMissionConfirm}
          onCancel={() => setShowLeaveConfirmDialog(false)}
        />,
        document.body
      )}

      {notification && createPortal(
        <NotificationToast
          message={notification.message}
          type={notification.type}
          duration={notification.type === 'success' ? 4000 : 5000}
          onClose={() => setNotification(null)}
        />,
        document.body
      )}

      {currentMission && !showHQBuildingPage ? (
        <TutorialReplayButton user={user} onReplay={() => setShowTutorialWorldMap(true)} />
      ) : null}

      {showTutorialWorldMap && currentMission && !showHQBuildingPage && createPortal(
        <TutorialMessageBox
          image="/images/tutorial_explain_4.png"
          text={t('warEffort.worldMapTutorialText')}
          onContinue={async () => {
            playSoundEffect('/sounds/click.mp3');
            if (!isReplayingWorldMapTutorial) {
              const token = localStorage.getItem('token');
              if (token) {
                await fetch(`${API_URL}/tutorial/mark-seen`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ messageKey: 'war_effort_world_map' }),
                });
                markSeenLocally('war_effort_world_map');
              }
            }
            setShowTutorialWorldMap(false);
          }}
          voiceKey="warEffort.worldMapTutorialText"
        />,
        document.body
      )}
    </div>
  );
}

export default WareEffort;
